<!-- Says, permanently, that this is not your data.

     A STRIP, NOT A TOOLBAR. This used to be a stock `IonToolbar` holding an
     `IonTitle`, on the reasoning that stock components inherit whatever the
     platform and palette decide. What they decided was 70px — a fifth of the
     first screen — for one sentence and one link: `ion-toolbar` carries a
     56-62px floor, `ion-title` adds its own `6px / 16px` of vertical padding
     on top, and the title box is narrow enough that the sentence wrapped to
     three lines inside it. Every custom property that could shrink the
     toolbar was set and the title's own geometry beat all of them.

     A caption is not a screen's furniture, so it does not need a toolbar's.
     This is a div with a colour, a line of small type, and no padding it was
     not asked for — about 26px, and it shrinks to fit rather than growing to
     a floor nobody chose.

     The LINK takes `currentColor` rather than the browser's blue: on the
     warning ground a default link measures 5.89:1 — legible, so this is not a
     contrast fix — but it would be the only element on screen wearing a colour
     nothing else in the app uses. The underline keeps it obviously a link. -->
<template>
  <div ref="el" class="demo-banner">
    Demo data · nothing is saved.
    <a href="/app">Open the real app</a>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const el = ref(null);
let observer = null;

/**
 * Make room for this banner.
 *
 * `ion-router-outlet` is `position: absolute; inset: 0`, so it does NOT flow
 * below a sibling — it fills the whole app and paints the page's own
 * `ion-header` at y=0, exactly where this banner already is. The banner is
 * later in paint order, so it wins, and the app's toolbar is invisible for as
 * long as demo mode is on.
 *
 * That is worse than it sounds. `/demo` is the surface the store screenshots
 * are taken from and the surface QA audits, so every picture of every app this
 * factory has produced lost its toolbar — the title, the settings button, the
 * help button — and nothing errored. It is only wrong in demo mode, which is
 * the one mode nobody develops in.
 *
 * Measured rather than hardcoded: the strip is one line on a phone and two on
 * a narrow one, and the outlet has to start under whichever it is.
 */
function measure() {
  const host = el.value?.$el ?? el.value;
  if (!host) return;
  const h = Math.ceil(host.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--demo-banner-h', `${h}px`);
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(measure);
    const host = el.value?.$el ?? el.value;
    if (host) observer.observe(host);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  document.documentElement.style.removeProperty('--demo-banner-h');
});
</script>

<!-- Not scoped: the rule has to reach ion-router-outlet, which is not this
     component's element. The variable is unset in the real app, so the
     fallback of 0 makes this inert everywhere except demo mode. -->
<style>
.demo-banner {
  position: relative;
  z-index: 2;
  padding: 5px var(--sp-4);
  background: var(--ion-color-warning, #FFC409);
  color: var(--ion-color-warning-contrast, #000000);
  font: 600 var(--fs-small)/1.35 var(--font-body);
  text-align: center;
  text-wrap: balance;
}

/* Inherit the strip's foreground rather than the browser's link blue. */
.demo-banner a {
  color: currentColor;
  text-decoration-thickness: from-font;
  text-underline-offset: 0.15em;
}

ion-app > ion-router-outlet {
  top: var(--demo-banner-h, 0);
}
</style>
